<template>
  <div class="drawer-settings" id="chatbot-node-configuration">
    <div class="drawer-settings-header">
      <h6 class="heading-h7 title">
        {{ title }}
        <ChannelHelpTooltip
          v-if="thereAreSettings"
          :node-type="type"
          :channel-type="currentChannel.id"
          :parent-node-type="settings.parent_type"
        />
      </h6>
      <CloseOutlined style="margin-right: 8px" @click="handleClose" />
    </div>

    <a-tooltip>
      <template #title v-if="isChatbotActive"> Desactiva el chatbot para editarlo </template>

      <div v-if="!thereAreSettings" class="loading">
        <LoadingOutlined />
      </div>

      <a-form
        v-else
        ref="form"
        :model="formState"
        :rules="rules"
        layout="vertical"
        class="drawer-settings-body"
      >
        <CustomScrollbar class="scroll" ref="scrollbarRef">
          <p
            class="body-2"
            style="margin-bottom: 8px; display: flex; justify-content: space-between"
          >
            Completa los campos requeridos

            <PreviewModal
              :node-type="type"
              :channel-type="currentChannel.id"
              :parent-node-type="settings.parent_type"
            />
          </p>
          <!--Drawer para NODOS PADRES-->
          <template v-if="[NodeType.Options, NodeType.Buttons, NodeType.List].includes(type)">
            <!--TITULO DE LISTAS-->
            <a-form-item v-if="type === NodeType.List" name="title" label="Título" required>
              <div class="item-emoji">
                <a-input
                  v-model:value="formState.title"
                  :show-count="{
                    formatter: () => formatterCount('title', type)
                  }"
                  :maxlength="maxLength('title', type)"
                  placeholder="Titulo"
                  @input="onInputRemoveComposed($event, 'title')"
                  :disabled="isChatbotActive"
                />
                <EmojiPicker
                  @onChangeEmoji="handleSelectEmoji($event, 'title')"
                  omitComposed
                  :icon-from-ant="true"
                  :disabled="disabledEmojiPicker('title', type)"
                  class="menu__option"
                />
              </div>
            </a-form-item>
            <!--MENSAJE PARA EL NODO OPCIONES-->
            <a-form-item
              :label="type === NodeType.Options ? 'Mensaje de opción múltiple' : 'Mensaje'"
              class="section-form-item"
              name="message"
            >
              <p
                v-if="type === NodeType.Options"
                class="help"
                style="margin-bottom: 8px; text-align: left"
              >
                Recuerda que será un mensaje de introducción para tus usuarios.
              </p>
              <counter-text
                :maxLength="maxLength('message', type)"
                :text="formState.message"
              >
                <a-mentions
                  v-model:value="formState.message"
                  placeholder="Escribe aquí"
                  :auto-size="{ minRows: 3, maxRows: 5 }"
                  :maxLength="maxLength('message', type)"
                  @change="onInput"
                  :disabled="isChatbotActive"
                  :rows="3"
                  :options="varsToUseFormatted"
                  :prefix="PREFIX_VARS"
                />
              </counter-text>
              <menu-options-content
                v-if="thereAreSettings && type !== NodeType.List"
                ref="menuOptions"
                :show-wait="false"
                :disabled-all="isChatbotActive"
                :disabled-vars="!varsToUseFormatted.length"
                :loading-vars="loadingVars"
                @onChangeEmoji="(emoji) => handleSetEmoji(emoji, 'message')"
                @onOpenVars="handleOpenVars('message')"
                @onChangeFile="handleChangeMedia"
                :default-media="settings"
                :disabled-media="isChatbotActive"
                :disabled-emoji="disabledEmojiPicker('message', type)"
              />
              <EmojiPicker
                v-else-if="type === NodeType.List"
                @onChangeEmoji="handleSelectEmoji($event, 'message')"
                omitComposed
                :icon-from-ant="true"
                :disabled="disabledEmojiPicker('message', type)"
                class="menu__option"
              />
            </a-form-item>

            <a-form-item
              v-if="type === NodeType.List"
              name="title_button"
              required
              :rules="{ required: true, message: 'Por favor ingresar Botón principal' }"
            >
              <template #label>
                Botón principal

                <a-tooltip
                  title="Este botón será aquel que despliegue la lista de ítems que podrá seleccionar el contacto."
                >
                  <InfoCircleOutlined style="margin-left: 5px" />
                </a-tooltip>
              </template>
              <div class="item-emoji">
                <a-input
                  v-model:value="formState.title_button"
                  :show-count="{
                    formatter: () => formatterCount('title_button', type)
                  }"
                  :maxlength="maxLength('title_button', type)"
                  placeholder="Menú"
                  @input="onInputRemoveComposed($event, 'title_button')"
                  :disabled="isChatbotActive"
                /> 
                <EmojiPicker
                  @onChangeEmoji="handleSelectEmoji($event, 'title_button')"
                  omitComposed
                  :icon-from-ant="true"
                  :disabled="disabledEmojiPicker('title_button', type)"
                  class="menu__option"
                />
              </div>
            </a-form-item>

            <a-form-item
              label="Mensaje por respuesta incorrecta"
              class="section-form-item"
              name="message_wrong_answer"
            >
              <p class="help" style="margin-bottom: 8px; text-align: left">
                Recuerda este mensaje se mostrará al cliente cuando se ingrese una opción diferente
                a las mostradas
              </p>
              <counter-text
                :maxLength="maxLength('message_wrong_answer', type)"
                :text="formState.message_wrong_answer"
              >
                <a-mentions
                  v-model:value="formState.message_wrong_answer"
                  :auto-size="{ minRows: 3, maxRows: 5 }"
                  placeholder="Escribe aquí"
                  :maxLength="maxLength('message_wrong_answer', type)"
                  :disabled="isChatbotActive"
                  @change="onInput"
                  :rows="3"
                  :options="varsToUseFormatted"
                  :prefix="PREFIX_VARS"
                />
              </counter-text>
              <menu-options-content
                v-if="thereAreSettings"
                ref="menuOptionsWrong"
                key-media="wrong_answer_media"
                :show-wait="false"
                :disabled-all="isChatbotActive"
                :disabled-vars="!varsToUseFormatted.length"
                :loading-vars="loadingVars"
                @onChangeEmoji="(emoji) => handleSetEmoji(emoji, 'message_wrong_answer')"
                @onOpenVars="handleOpenVars('message_wrong_answer')"
                @onChangeFile="handleChangeMedia"
                :default-media="settings"
                :disabled-media="isChatbotActive"
                :disabled-emoji="disabledEmojiPicker('message_wrong_answer', type)"
              />
            </a-form-item>
            <!--CAMPO PARA GUARDAR VARIABLE-->
            <picker-vars
              :nodeId="settings.id"
              :chatbotId="chatbotId"
              :defaultVar="varUsedInNode"
              :checked="formState.contains_variable"
              hideExtra
              @onChange="updateNodesList"
              @onChecked="handleSetContainsVariable"
              ref="pickerVars"
              @onChangeVar="onInput(); changePickerVars();"
              :disabled="isChatbotActive"
            />
          </template>

          <!-- Drawer para una seccion de lista -->
          <template v-else-if="type === NodeType.ListSection">
            <a-form-item
              name="title"
              :label="`Sección ${settings.order} - Ingresa el nombre de tu selección`"
              required
            >
              <div class="item-emoji">
                <a-input
                  v-model:value="formState.title"
                  :show-count="{
                    formatter: () => formatterCount('title', type)
                  }"
                  :maxlength="maxLength('title', type)"
                  placeholder="Titulo"
                  @input="onInputRemoveEmojis($event, 'title')"
                  :disabled="isChatbotActive"
                />
                <!--<EmojiPicker
                  @onChangeEmoji="handleSelectEmoji($event, 'title')"
                  omitComposed
                  :icon-from-ant="true"
                  :disabled="disabledEmojiPicker('title', type)"
                  class="menu__option"
                />-->
              </div>
            </a-form-item>
          </template>

          <!-- Drawer para una tarjeta -->
          <template v-if="[NodeType.Card, NodeType.CardSection].includes(type)">
            <template v-if="type === NodeType.CardSection">
              <a-form-item name="picture" label="Imagen adjunta" required>
                <ArchivePicker
                  v-model:value="formState.picture"
                  :maxMBSize="5"
                  :supported-types="['.png', '.jpg', '.jpeg']"
                  :disabled="isChatbotActive"
                  @update:value="onInput"
                />

                <div v-if="typeof formState.picture === 'string'" class="image-details">
                  <PaperClipOutlined />

                  <a-button type="link" @click="handlePreview">
                    {{ formState.picture.split("/").at(-1) }}
                  </a-button>
                </div>
              </a-form-item>

              <a-form-item name="title" label="Título" required>
                <a-input
                  v-model:value="formState.title"
                  :show-count="{
                    formatter: () => formatterCount('title', type)
                  }"
                  :maxlength="maxLength('title', type)"
                  placeholder="Titulo"
                  @input="onInputRemoveComposed($event, 'title')"
                  :disabled="isChatbotActive"
                />
              </a-form-item>

              <a-form-item label="Mensaje de tarjeta" class="section-form-item" name="message">
                <counter-text
                  :maxLength="maxLength('message', type)"
                  :text="formState.message"
                >
                  <a-mentions
                    v-model:value="formState.message"
                    placeholder="Escribe aquí"
                    :maxLength="maxLength('message', type)"
                    @change="onInput"
                    :disabled="isChatbotActive"
                    :rows="3"
                    :options="varsToUseFormatted"
                    :prefix="PREFIX_VARS"
                  />
                </counter-text>
                <menu-options-content
                  v-if="thereAreSettings"
                  ref="menuOptions"
                  :show-wait="false"
                  :disabled-all="isChatbotActive"
                  :disabled-vars="!varsToUseFormatted.length"
                  :loading-vars="loadingVars"
                  @onChangeEmoji="(emoji) => handleSetEmoji(emoji, 'message')"
                  @onOpenVars="handleOpenVars('message')"
                  :show-media="false"
                  :disabled-media="isChatbotActive"
                  :disabled-emoji="disabledEmojiPicker('message', type)"
                />
              </a-form-item>
            </template>

            <a-form-item
              v-else
              label="Mensaje por respuesta incorrecta"
              class="section-form-item"
              name="message_wrong_answer"
            >
              <p class="help" style="margin-bottom: 8px; text-align: left">
                Recuerda este mensaje se mostrará al contacto cuando mande un nuevo mensaje y no
                seleccione ninguno de los botones mostrados.
              </p>
              <counter-text
                :maxLength="maxLength('message_wrong_answer', type)"
                :text="formState.message_wrong_answer"
              >
                <a-mentions
                  v-model:value="formState.message_wrong_answer"
                  placeholder="Escribe aquí"
                  :maxLength="maxLength('message_wrong_answer', type)"
                  :disabled="isChatbotActive"
                  @change="onInput"
                  :rows="3"
                  :options="varsToUseFormatted"
                  :prefix="PREFIX_VARS"
                />
              </counter-text>
              <menu-options-content
                v-if="thereAreSettings"
                ref="menuOptions"
                :show-wait="false"
                :disabled-all="isChatbotActive"
                :disabled-vars="!varsToUseFormatted.length"
                :loading-vars="loadingVars"
                @onChangeEmoji="(emoji) => handleSetEmoji(emoji, 'message_wrong_answer')"
                @onOpenVars="handleOpenVars('message_wrong_answer')"
                key-media="wrong_answer_media"
                @onChangeFile="handleChangeMedia"
                :default-media="settings"
                :disabled-media="isChatbotActive"
                :disabled-emoji="disabledEmojiPicker('message_wrong_answer', type)"
              />
            </a-form-item>
            <!--CAMPO PARA GUARDAR VARIABLE-->
            <picker-vars
              v-if="type === NodeType.Card"
              :nodeId="settings.id"
              :chatbotId="chatbotId"
              :defaultVar="varUsedInNode"
              :checked="formState.contains_variable"
              @onChange="updateNodesList"
              @onChecked="handleSetContainsVariable"
              ref="pickerVars"
              @onChangeVar="onInput(); changePickerVars();"
              :disabled="isChatbotActive"
            />
          </template>

          <!--Drawer para mensajes-->
          <template v-else-if="nodeTypeMessage.includes(type)">
            <!-- Campo de texto para mostrar -->
            <a-form-item
              label="Mensaje"
              class="section-form-item section-form-item--message"
              name="message"
            >
              <p class="help" style="margin-bottom: 8px; text-align: left">
                Recuerda que será un mensaje de tipo texto para tus usuarios.
              </p>
              <counter-text
                :maxLength="maxLength('message', type)"
                :text="formState.message"
              >
                <a-mentions
                  v-model:value="formState.message"
                  placeholder="Escribe aquí"
                  :auto-size="{ minRows: 3, maxRows: 5 }"
                  :disabled="isChatbotActive"
                  @change="onInput"
                  :rows="3"
                  :options="varsToUseFormatted"
                  :prefix="PREFIX_VARS"
                  :maxLength="maxLength('message', type)"
                />
              </counter-text>
              <menu-options-content
                v-if="thereAreSettings"
                ref="menuOptions"
                :disabled-all="isChatbotActive"
                @onChangeWait="handleChangeWait"
                :value-check="formState.wait_answer"
                :disabled-wait-answer="
                  action === BotActions.FinishChatbot ||
                  isChatbotActive ||
                  formState.contains_variable
                "
                :disabled-vars="!varsToUseFormatted.length"
                :loading-vars="loadingVars"
                @onChangeEmoji="(emoji) => handleSetEmoji(emoji, 'message')"
                @onOpenVars="handleOpenVars('message')"
                @onChangeFile="handleChangeMedia"
                :default-media="settings"
                :disabled-media="isChatbotActive"
                :disabled-emoji="disabledEmojiPicker('message', type)"
              />
            </a-form-item>
            <!--CAMPO PARA GUARDAR VARIABLE-->
            <picker-vars
              :nodeId="settings.id"
              :chatbotId="chatbotId"
              :defaultVar="varUsedInNode"
              :checked="formState.contains_variable"
              @onChange="updateNodesList"
              @onChecked="handleSetContainsVariable"
              ref="pickerVars"
              @onChangeVar="onInput(); changePickerVars();"
              :disabled="isChatbotActive"
            />
            <!-- Lista de radiobutton de acciones -->
            <a-form-item
              label="Selecciona la siguiente acción a realizar"
              class="section-form-item"
              name="action"
            >
              <ActionPicker
                v-model:value="formState.action"
                :disabled="isChatbotActive"
                :channel-type="currentChannel.id"
                :node-type="settings.type"
                @change="handleChangeAction"
                style="width: 100%"
              />
            </a-form-item>
          </template>

          <!--Drawer para una opcion, un botón y un item de sección-->
          <template v-else>
            <template v-if="type === NodeType.ListSectionItem">
              <a-form-item
                :label="`Ítem ${settings.order} - Ingresa el nombre de tu ítem`"
                class="section-form-item"
                name="title"
                required
              >
                <div class="item-emoji">
                  <a-input
                    v-model:value="formState.title"
                    @input="onInputRemoveEmojis($event, 'title')"
                    placeholder="Escribir aquí"
                    :show-count="{
                      formatter: () => formatterCount('title', type)
                    }"
                    :maxlength="maxLength('title', type)"
                    :disabled="isChatbotActive"
                  />
                  <!-- <EmojiPicker
                    @onChangeEmoji="handleSelectEmoji($event, 'title')"
                    omitComposed
                    :icon-from-ant="true"
                    :disabled="disabledEmojiPicker('title', type)"
                    class="menu__option"
                  /> -->
                </div>
              </a-form-item>

              <a-form-item
                label="Descripción"
                class="section-form-item"
                name="description"
                :rules="{ required: false }"
              >
                <div class="item-emoji">
                  <a-input
                    v-model:value="formState.description"
                    @input="onInputRemoveComposed($event, 'description')"
                    placeholder="Escribir aquí"
                    :show-count="{
                      formatter: () => formatterCount('description', type)
                    }"
                    :maxlength="maxLength('description', type)"
                    :disabled="isChatbotActive"
                  />
                  <EmojiPicker
                    @onChangeEmoji="handleSelectEmoji($event, 'description')"
                    omitComposed :icon-from-ant="true" class="menu__option"
                    :disabled="disabledEmojiPicker('description', type)"
                  />
                </div>
              </a-form-item>
            </template>

            <template v-else-if="hasSimpleOptionConfig.includes(type)">
              <a-form-item
                v-if="type === NodeType.ButtonOption"
                :label="`Botón ${settings.order} - Ingresa el nombre de tu botón`"
                class="section-form-item"
                name="title"
              >
                <div class="item-emoji">
                  <a-input
                    v-model:value="formState.title"
                    @input="onInputButtonSanitize($event, 'title')"
                    :show-count="{
                      formatter: () => formatterCount('title', type)
                    }"
                    ref="inputButtonRef"
                    placeholder="Escribir aquí"
                    :maxLength="maxLength('title', type)"
                    :disabled="isChatbotActive"
                  />
                  <EmojiPicker
                    @onChangeEmoji="handleSelectEmoji($event, 'title')"
                    omitComposed
                    :icon-from-ant="true"
                    :disabled="disabledEmojiPicker('title', type)"
                    class="menu__option" 
                  />
                </div>
              </a-form-item>

              <a-form-item
                v-else-if="![NodeType.OptionConsultApi, NodeType.WrongOptionAPI].includes(type)"
                :label="`Opción ${settings.order}`"
                class="section-form-item"
                name="description"
              >
                <a-input
                  v-model:value="formState.description"
                  @input="onInputRemoveComposed($event, 'description')"
                  :show-count="{
                    formatter: () => formatterCount('description', type)
                  }"
                  placeholder="Escribir aquí"
                  :maxLength="maxLength('description', type)"
                  :disabled="isChatbotActive"
                />
              </a-form-item>
            </template>
            <a-form-item
              v-if="
                ![NodeType.List, NodeType.ListSection, NodeType.Options, NodeType.Buttons].includes(
                  type
                )
              "
              label="Selecciona la siguiente acción a realizar"
              class="section-form-item"
              name="action"
            >
              <ActionPicker
                v-model:value="formState.action"
                :disabled="isChatbotActive"
                :channel-type="currentChannel.id"
                :node-type="settings.type"
                @change="handleChangeAction"
                style="width: 100%"
              />
            </a-form-item>
          </template>
          <!--Si la accion es transferir--->
          <template v-if="action === BotActions.TransferToQueue">
            <CampaignPicker
              chatbot-picker
              default-behavior
              with-out-entity
              with-name
              :show-all="false"
              label="Selecciona una campaña"
              :disabled="isChatbotActive"
              :value="formState.portfolio"
              @update:value="formState.portfolio = $event"
            />

            <QueuePicker
              default-behavior
              with-out-portfolio
              :show-all="false"
              :portfolio="formState.portfolio"
              :disabled="isChatbotActive"
              v-model:value="formState.queue"
              @change="handleQueueChange"
              :showTooltip="false"
            >
              <template #label>
                Selecciona una cola

                <a-tooltip
                  title="Se listarán solo aquellas colas asociadas a la campaña seleccionada."
                >
                  <InfoCircleOutlined style="margin-left: 5px" />
                </a-tooltip>
              </template>
            </QueuePicker>
            <!-- This is just for Single Option because a message cant show another message of transfering -->
            <template v-if="hasSimpleOptionConfig.includes(type)">
              <a-switch
                class="switch-form"
                size="small"
                v-model:checked="activeMessageTransfer"
                :disabled="isChatbotActive"
                @change="setIsSavedItem(false)"
              />
              <a-form-item
                label="Mensaje de transferencia"
                class="section-form-item"
                name="transfer_queue_message"
              >
                <counter-text
                  :maxLength="maxLength('transfer_queue_message', type)"
                  :text="formState.transfer_queue_message"
                >
                  <a-mentions
                    v-model:value="formState.transfer_queue_message"
                    placeholder="Escribe aquí"
                    @change="onInput"
                    :disabled="!activeMessageTransfer || isChatbotActive"
                    :maxLength="maxLength('transfer_queue_message', type)"
                    :rows="3"
                    :options="varsToUseFormatted"
                    :prefix="PREFIX_VARS"
                  />
                </counter-text>
                <menu-options-content
                  v-if="thereAreSettings"
                  ref="menuOptions"
                  :show-wait="false"
                  :disabled-all="isChatbotActive"
                  :disabled-vars="!varsToUseFormatted.length"
                  :loading-vars="loadingVars"
                  @onChangeEmoji="(emoji) => handleSetEmoji(emoji, 'transfer_queue_message')"
                  @onOpenVars="handleOpenVars('transfer_queue_message')"
                  key-media="transfer_queue_media"
                  @onChangeFile="handleChangeMedia"
                  :default-media="settings"
                  :disabled-media="!activeMessageTransfer || isChatbotActive"
                  :disabled-emoji="disabledEmojiPicker('transfer_queue_message', type)"
                />
              </a-form-item>
            </template>
          </template>

          <template
            v-else-if="
              action === BotActions.FinishChatbot && 
              hasSimpleOptionConfig.includes(type) &&
              ![NodeType.OptionConsultApi, NodeType.WrongOptionAPI].includes(type)
            "
          >
            <a-form-item label="Mensaje final" class="section-form-item" name="message">
              <p class="help mrg-bottom-8 text--left" style="margin-top: 4px">
                Recuerda que este mensaje se mostrará al cliente cuando elija esta opción.
              </p>
              <counter-text :maxLength="maxLength('message', type)" :text="formState.message">
                <a-mentions
                  v-model:value="formState.message"
                  :disabled="isChatbotActive"
                  placeholder="Escribe aquí"
                  @change="onInput"
                  :rows="3"
                  :options="varsToUseFormatted"
                  :prefix="PREFIX_VARS"
                  :maxLength="maxLength('message', type)"
                />
              </counter-text>
              <menu-options-content
                v-if="thereAreSettings"
                ref="menuOptions"
                :show-wait="false"
                :disabled-all="isChatbotActive"
                :disabled-vars="!varsToUseFormatted.length"
                @onOpenVars="handleOpenVars('message')"
                @onChangeEmoji="(emoji) => handleSetEmoji(emoji, 'message')"
                @onChangeFile="handleChangeMedia"
                :default-media="settings"
                :disabled-media="isChatbotActive"
                :disabled-emoji="disabledEmojiPicker('message', type)"
              />
            </a-form-item>
          </template>
          <!-- <template
            v-if="action === BotActions.BackToPreviousMenu && hasSimpleOptionConfig.includes(type)"
          >
            <p class="body-2 text-align-left">
              Recuerda que se volverán a mostrar las opciones del menú anterior.
            </p>
          </template> -->
          <template v-else-if="description">
            <p class="body-2 text-align-left">
              {{ description }}
            </p>
          </template>

          <template v-if="action === BotActions.BackToPreviousMenu">
            <a-divider type="horizontal" class="divider" />

            <BackToNodePicker
              v-model:value="formState.node_to_back"
              :node-id="settings.id"
              @update:value="onInput"
            />
          </template>

          <div v-if="action == BotActions.ContinueConnectorApi">
            <h3>
              Configuración del conector de API
            </h3>

            <a-form-item
              label="Selecciona tu conector de API"
              name="api_connector"
              :rules="[{ required: true, message: 'Por favor selecciona un conector de API' }]"
            >
              <a-select
                v-model:value="formState.api_connector"
                @change="setIsSavedItem(false); changeConnectorApi();"
                :isDisabled="isChatbotActive"
                placeholder="Seleccione la acción"
              >
                <a-select-option v-for="option in optionsConnectorApi" :key="option.id">
                  <span>{{ option?.name }}</span>
                  <p class="select--info">{{ ResponseTypesText[Number(option?.data_type)] }}</p>
                </a-select-option>
              </a-select>
            </a-form-item>

            <!-- CAMBIAR CON VALIDACIONES -->
            <a-form-item
              name="api_input_variable"
              :rules="[{ required: true, message: 'Por favor selecciona una variable de entrada' }]"
            >
              <template #label>
                Selecciona una variable de entrada

                <a-tooltip
                  title="Solo se listan aquellas variables seleccionadas para guardar una respuesta. "
                >
                  <InfoCircleOutlined style="margin-left: 5px" />
                </a-tooltip>
              </template>
              <a-select
                class="select-input-variable"
                v-model:value="formState.api_input_variable"
                @change="setIsSavedItem(false)"
                :notFoundContent="renderEmpty('Aún no hay variables disponibles')"
                placeholder="Selecciona una variable de entrada"
              >
                <a-select-option
                  v-for="varManage in variablesInboundOptions"
                  :key="varManage.id"
                >
                  {{ varManage.name }}
                  <em v-if="varManage.deleted">(Variable eliminada)</em>
                </a-select-option>
              </a-select>
            </a-form-item>

            <!-- CAMBIAR CON VALIDACIONES -->
            <a-form-item
              name="api_output_variable"
              v-if="connectorSelected?.data_type == ResponseType.String"
              :rules="[{ required: true, message: 'Por favor selecciona una variable a guardar en API' }]"
            >
              <template #label>
                Selecciona una variable a guardar en API

                <a-tooltip
                  title="Solo se listan variables creadas desde el Gestor de variables. "
                >
                  <InfoCircleOutlined style="margin-left: 5px" />
                </a-tooltip>
              </template>
              <a-select
                v-model:value="formState.api_output_variable"
                @change="setIsSavedItem(false)"
                placeholder="Selecciona una variable a guardar en API"
              >
                <a-select-option
                  v-for="varManage in variablesOptions"
                  :key="varManage.id"
                  :disabled="varManage.deleted || varManage.id == formState.api_input_variable"
                >
                  <a-tooltip>
                    <template #title v-if="varManage.id == formState.api_input_variable">
                      Esta variable ya está siendo usada como variable de entrada.
                    </template>
                    <div style="width: 100%">
                      {{ varManage.name }}
                      <em v-if="varManage.deleted">(Variable eliminada)</em>
                    </div>
                  </a-tooltip>
                </a-select-option>
              </a-select>
            </a-form-item>

            <a-form-item
              v-if="formState.api_connector && connectorSelected?.data_type != ResponseType.String"
              label="Valores del tipo numérico"
              class="form-item"
              name="connector_value"
            >
              <a-input
                v-if="connectorSelected?.data_type == ResponseType.Number"
                :value="connectorSelected?.data_value"
                disabled
              />
              <a-input v-else placeholder="Verdadero/falso" disabled />
            </a-form-item>

            <a-form-item
              v-if="connectorSelected?.data_type == ResponseType.String"
              label="Selecciona la siguiente acción a realizar"
              class="section-form-item"
              name="action_next"
              :rules="[{ required: true, message: 'Por favor selecciona la siguiente acción' }]"
            >
              <ActionPicker
                v-model:value="formState.action_next"
                :disabled="isChatbotActive"
                :channel-type="currentChannel.id"
                :node-type="settings.type"
                :is-connector-string="true"
                @change="setIsSavedItem(false)"
                style="width: 100%"
              />
            </a-form-item>
          </div>
        </CustomScrollbar>
        <div class="drawer-settings-footer">
          <a-button :disabled="isSavedNode" @click="handleCancel"> Cancelar </a-button>

          <a-button
            type="primary"
            style="margin-left: 12px"
            html-type="submit"
            id="edit-node-save"
            :disabled="isSavedNode"
            :loading="loading"
            @click="handleSubmit"
          >
            Guardar
          </a-button>
        </div>
      </a-form>
    </a-tooltip>
  </div>
</template>

<script>
import { defineComponent, ref, reactive, createVNode, inject, computed, h } from "vue";
import { mapGetters, mapActions, mapMutations } from "vuex";
import {
  CloseOutlined,
  InfoCircleOutlined,
  ExclamationCircleOutlined,
  LoadingOutlined,
  PaperClipOutlined,
} from "@ant-design/icons-vue";

import {
  NodeType,
  BotActions,
  hasSimpleOptionConfig,
  PREFIX_VARS,
  NodeTypeTextSize,
  hasConnectorOptionConfig,
} from "@/app/chatbots/utils/enums";
import { updateNode, deleteNode } from "@/app/chatbots/services";
import { hasChildNodes } from "@/app/chatbots/utils/helpers";
import { ChannelType } from "@/app/shared/utils/enums";
import { filterVarsOfParents } from "@/app/chatbots/utils/varsTree";

import CustomScrollbar from "@/app/shared/components/organisms/CustomScrollbar.vue";
import CampaignPicker from "@/app/shared/components/pickers/CampaignPicker.vue";
import QueuePicker from "@/app/shared/components/pickers/QueuePicker.vue";
import CounterText from "@/app/shared/components/molecules/CounterText.vue";
import ActionPicker from "@/app/chatbots/components/pickers/ActionPicker.vue";
import PreviewModal from "@/app/chatbots/components/modals/PreviewModal.vue";
import ChannelHelpTooltip from "@/app/chatbots/components/icons/ChannelHelpTooltip.vue";
import ArchivePicker from "@/app/shared/components/molecules/ArchivePicker.vue";
import MenuOptionsContent from "@/app/chatbots/components/molecules/MenuOptionsContent.vue";
import PickerVars from "@/app/chatbots/components/pickers/PickerVars.vue";
import BackToNodePicker from "@/app/chatbots/components/pickers/BackToNodePicker.vue";
import EmojiPicker from "@/app/shared/components/pickers/EmojiPicker.vue";
import DisabledContainer from "@/app/shared/components/molecules/DisabledContainer.vue";
import { ResponseType, ResponseTypesText } from "@/app/ivr/bot/utils/enums";
import { Empty } from "ant-design-vue";

export default defineComponent({
  name: "DrawerSettings",
  props: {
    settings: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    chatbotId: {
      type: String,
      required: true,
    },
    channels: {
      type: Array,
      required: true,
    },
    currentChannel: {
      type: Object,
      default: () => ({}),
    },
    optionsConnectorApi: {
      type: Array,
      required: true,
    },
  },
  components: {
    CustomScrollbar,
    CounterText,
    CloseOutlined,
    InfoCircleOutlined,
    LoadingOutlined,
    CampaignPicker,
    QueuePicker,
    ActionPicker,
    PreviewModal,
    ChannelHelpTooltip,
    ArchivePicker,
    PaperClipOutlined,
    MenuOptionsContent,
    PickerVars,
    BackToNodePicker,
    EmojiPicker,
    DisabledContainer,
  },
  setup(props) {
    const activeMessageTransfer = ref(false);
    const isChatbotActive = inject("isChatbotActive");
    const varsManage = inject("varsManage");
    const EMOJI_VALUE = 2;

    const pickerVars = ref();
    const menuOptions = ref();
    const menuOptionsWrong = ref(false);
    const form = ref();
    const formState = reactive({
      message: "",
      message_wrong_answer: "",
      wait_answer: false,
      action: null,
      description: "",
      portfolio: null,
      queue: null,
      contains_variable: false,
      node_to_back: undefined,
      api_connector: "",
      action_next: 1,
    });
    const mediaData = ref();
    const varsToUse = ref([]);

    const rules = computed(() => {
      const baseRules = {
        message: [
          { required: true, message: "Campo requerido" },
          { whitespace: true, message: "No se admiten espacios en blanco " },
        ],
        message_wrong_answer: [
          {
            required: true,
            message: "Por favor rellene el campo",
          },
          {
            whitespace: true,
            message: "No se admiten espacios en blanco.",
          },
        ],
        transfer_queue_message: [
          {
            required: activeMessageTransfer.value,
            message: "Por favor rellene el campo",
          },
          {
            whitespace: activeMessageTransfer.value,
            message: "No se admiten espacios en blanco.",
          },
        ],
        action: [{ required: true, message: "Campo requerido" }],
        description: [
          {
            required: true,
            message: "Por favor rellene el campo",
          },
          {
            whitespace: true,
            message: "No se admiten espacios en blanco.",
          },
        ],
        queue: [{ required: true, message: "Completa el campo" }],
        title: [{ required: true, message: "Completa el campo" }],
      };

      // if (
      //   [NodeType.List, NodeType.ListSection, NodeType.ListSectionItem].includes(
      //     props.settings.type
      //   )
      // ) {
      //   baseRules.title.push({
      //     pattern: /^([A-Za-z0-9-%ñÑ&À-ú\s])+$/g,
      //     message: "No se admiten caracteres especiales",
      //   });
      //}

      return baseRules;
    });

    /**
     * @return String[] - tipos del nodo mensaje
     */
    const nodeTypeMessage = computed(() => {
      return [
        NodeType.Message,
        NodeType.MessageWithImage,
        NodeType.MessageWithDocument,
        NodeType.MessageWithVideo,
      ];
    });

    const formatGraphemesEmojis = (content) => {
      const segmenter = new Intl.Segmenter("en", { granularity: "grapheme" });
      const graphemes = [...segmenter.segment(content)].map((segment) => segment.segment);

      return graphemes;
    };

    const customCount = (value) => {
      // Omitir caracteres especiales de formato en el contador
      const cleanContent = value

      return formatGraphemesEmojis(cleanContent).reduce((acc, char) => {
        const isEmoji =
          /[\p{Emoji_Presentation}\p{Extended_Pictographic}\u{1F3FB}-\u{1F3FF}\u{1F9B0}-\u{1F9B3}]|\p{Emoji}\uFE0F/u.test(
            char
          );
        return acc + (isEmoji ? EMOJI_VALUE : 1);
      }, 0);
    };
    
    const connectorSelected = computed(() => {
      if (formState.api_connector && props.optionsConnectorApi)
        return props.optionsConnectorApi.find(el => el.id == formState.api_connector);

      return null;
    });

    const variableSelected = computed(() => pickerVars.value?.formState?.variable);
    const variablesOptions = computed(() => pickerVars.value?.varsManageFormatted);
    const variablesInboundOptions = computed(() => {
      const option = variablesOptions.value?.find(
        (el) => el.id == variableSelected.value
      );

      const vars = varsToUse.value || [];

      return option ? [option, ...vars] : [...vars];
    });

    const renderEmpty = (description) => 
      h("div", { class: "select-empty-custom" }, [
        h(Empty, {
          image: Empty.PRESENTED_IMAGE_SIMPLE,
          description: false,
        }),
        h("span", { class: "select-empty-custom__text" }, description),
      ]);

    const changeConnectorApi = () => {
      if (connectorSelected.value?.data_type == ResponseType.String) {
        formState.action_next = 1;
      } else
        formState.action_next = null;
    }

    return {
      isChatbotActive,
      activeMessageTransfer,
      form,
      formState,
      rules,
      NodeType,
      BotActions,
      hasSimpleOptionConfig,
      ChannelType,
      nodeTypeMessage,
      mediaData,
      varsManage,
      pickerVars,
      menuOptions,
      menuOptionsWrong,
      customCount,
      NodeTypeTextSize,
      connectorSelected,
      ResponseTypesText,
      ResponseType,
      variablesOptions,
      variableSelected,
      variablesInboundOptions,
      renderEmpty,
      changeConnectorApi,
      varsToUse,
    };
  },
  data: () => ({
    PREFIX_VARS,
    action: null,
    radioStyle: {
      display: "flex",
      alignItems: "center",
      marginBottom: "2px",
    },
    isSavedNode: true, // si no hay cambios estara en false, si hay cambios en true
    isChangeAction: false,
    loading: false, // loading de boton de guardar
    option: {},
    nodeIdAction: null,
    waitAnswer: false,
    // varsToUse: [],
    loadingVars: false,
  }),
  created() {
    this.form?.resetFields();
    this.setValues();
  },
  beforeUnmount() {
    this.handleClose();
  },
  watch: {
    settings: {
      deep: true,
      handler() {
        // espera a que se termine de renderizar el componente
        this.$nextTick(() => {
          // resetea los valores del formulario
          this.form?.resetFields();
          this.mediaData = null;
          delete this.formState.type;
          // setea los nuevos valores
          this.setValues();

          this.filtersVarsToUse();

          this.$refs.scrollbarRef?.scrollToTop();
        });
      },
    },
  },
  computed: {
    ...mapGetters(["nodes", "profile", "companyLines"]),
    /**
     * @returns {String}
     */
    queuesOptions() {
      return this.$store.getters["getQueuesSelectOptions"];
    },
    treeNodes() {
      return this.$store.getters["getChatbotNodes"][0];
    },
    /**
     * @returns {Boolean} - verifica si hay configuraciones
     */
    thereAreSettings() {
      return Object.keys(this.settings).length !== 0;
    },
    // TODO: Llevar todo esto a utils
    title() {
      let title = "Configurar opción";

      if (this.settings.type === NodeType.Options) title = "Configurar opciones múltiples";
      if (this.nodeTypeMessage.includes(this.settings.type)) title = "Configurar mensaje";
      if (this.settings.type === NodeType.Buttons) title = "Configurar botones";
      if (this.settings.type === NodeType.ButtonOption)
        title =
          this.settings.parent_type === NodeType.Buttons
            ? "Configurar botones"
            : "Configurar tarjetas con botones";
      if (this.settings.type === NodeType.List) title = "Configurar listas";
      if (this.settings.type === NodeType.ListSection) title = "Configurar listas";
      if (this.settings.type === NodeType.ListSectionItem) title = "Configurar listas";
      if (this.settings.type === NodeType.Card) title = "Configurar tarjetas con botones";
      if (this.settings.type === NodeType.CardSection) title = "Configurar tarjetas con botones";
      if (this.settings.type === NodeType.OptionConsultApi) title = `Configurar validación_de_cliente - ${this.settings.referencial_name}`;
      if (this.settings.type === NodeType.WrongOptionAPI) title = `Configurar Opción error en API`;
      return title;
    },
    /**
     * @returns {String}
     */
    type() {
      return this.settings.type;
    },
    /**
     * FIltra las colas disponibles para mostrar según su canal asignado
     * @returns {Object[]}
     */
    chatbotLines() {
      const linesFilter = [];
      this.companyLines.forEach((companyLine) => {
        let isHere = false;
        isHere = this.channels.every((channel) => {
          return companyLine.channels_companies?.some(
            (channel_company) => channel_company._id === channel._id
          );
        });
        if (isHere || companyLine.default) {
          linesFilter.push({ key: companyLine._id, title: companyLine.name });
        }
      });
      return linesFilter;
    },
    /**
     * @returns {Boolean}
     */
    allowedRollback() {
      if (this.nodeTypeMessage.includes(this.type)) return false;
      if (this.settings.node === this.treeNodes.id || this.settings.id === this.treeNodes.id)
        return false;
      return true;
    },
    /**
     * @returns {String}
     */
    typeAction() {
      return this.settings?.action;
    },
    /**
     * @returns {String}
     */
    description() {
      switch (this.action) {
        case BotActions.ContinueToOptions:
          return "Añadiremos nuevas opciones múltiples para continuar con el flujo de tu chatbot. Recuerda guardar tus cambios.";
        case BotActions.AddMessage:
          return "Añadiremos un nuevo mensaje para continuar el flujo.";
        case BotActions.TransferToQueue:
          return "El ticket será transferido a la cola seleccionada.";
        case BotActions.FinishChatbot:
          return "El ticket será finalizado y cerrado.";
        default:
          return null;
      }
    },
    /**
     * Variables formateadas para que sean usadas en los mentions
     */
    varsToUseFormatted() {
      return this.varsToUse.map((item) => {
        const newObj = {};
        newObj.key = `${item.id}`;
        newObj.value = item.name;
        newObj.label = item.name;
        return newObj;
      });
    },
    /**
     * Variable usada en el nodo
     */
    varUsedInNode() {
      if (!this.settings || !this.settings.variable) return;
      const varUsed = {
        id_in_node: this.settings.variable.id,
        ...this.settings.variable.variable,
      };
      return varUsed;
    },
  },
  methods: {
    ...mapActions([
      "addNodeOptions",
      "updateOption",
      "addOptionToNode",
      "generateTreeNodes",
      "updateNodeOptions",
      "addNodeMessage",
      "updateNodeMessage",
    ]),
    ...mapMutations(["SET_IS_SAVED_NODE", "SUM_CHANGES", "SET_SELECTED_NODE_ID"]),

    async filtersVarsToUse() {
      if (!this.settings) return [];
      this.loadingVars = true;
      const variables = await filterVarsOfParents(
        this.settings.node,
        this.settings.parent_type,
        this.treeNodes
      );
      this.varsToUse = variables;
      console.log(this.varsToUse, 'vars to use');
      this.loadingVars = false;
    },

    /**
     * Cambia de accion
     * @param {String} value - transfer, contine_message, continue_options, none, rollback
     */
    handleChangeAction(value) {
      this.action = value;
      this.formState.api_input_variable = null;
      this.formState.api_connector = null;
      this.formState.api_output_variable = null;
      this.formState.action_next = null;
      this.setIsSavedItem(false);
      this.isChangeAction = true;
    },
    isEmojiComposed(emoji) {
      console.log("emoji: ", emoji);
      console.log("is composed: ", emoji.includes("\u200D"));
      // El ZWJ indica que es un emoji compuesto
      return emoji.includes("\u200D");
    },
    handleSelectEmoji(emojiMetadata, fieldName) {
      console.log("emojiMetadata", emojiMetadata);

      // Validar que el campo existe en formState y es texto
      if (this.formState[fieldName] !== undefined && typeof this.formState[fieldName] === 'string') {
        // validate is emoji composed
        if (this.isEmojiComposed(emojiMetadata.i)) return;
        this.formState[fieldName] += emojiMetadata.i;
        this.setIsSavedItem(false);
      } else {
        console.warn(`El campo ${fieldName} no existe en formState o no es un string`);
      }

      // Mantener el foco en el input asociado (opcional)
      if (this.inputButtonRef) {
        this.inputButtonRef.focus();
      }
    },
    handlePreview() {
      this.$emit("preview", this.formState.picture);
    },
    // setea los valores al formulario
    setValues() {
      this.action = this.typeAction; // poner esto antes de setear mensaje y el tipo
      // console.log('this.settings: ', this.settings);
      setTimeout(() => {
        this.formState.description = this.settings.description;
        this.formState.action = this.typeAction;
        this.formState.message = this.settings.message;
        this.formState.portfolio = this.settings.portfolio?.id;
        this.formState.queue = this.settings.queue?.id;
        this.formState.transfer_queue_message = this.settings.transfer_queue_message;
        this.activeMessageTransfer = Boolean(this.settings.transfer_queue_message);
        this.formState.wait_answer = this.settings.wait_answer;
        this.waitAnswer = this.settings.wait_answer;
        this.formState.message_wrong_answer = this.settings.message_wrong_answer;
        this.formState.title = this.settings.title;
        this.formState.title_button = this.settings.title_button;
        this.formState.picture = this.settings.picture;
        this.formState.contains_variable = this.settings.contains_variable;
        this.formState.node_to_back = this.settings.node_to_back;
        this.formState.api_connector = this.settings?.api_connector?.id;
        this.formState.api_input_variable = this.settings?.api_input_variable?.id;
        this.formState.api_output_variable = this.settings?.api_output_variable?.id;
        this.formState.action_next = this.settings?.action_next;
      }, 0);
    },
    /**
     * Modal de confirmacion
     * @param {String} title
     * @param {String} content
     * @param {String} okText
     * @param {String} cancelText
     * @param {Function} onOkey
     * @param {Function} onCancel
     */
    showConfirm(title, content, okText, fnOk, fnCancel) {
      this.$confirm({
        title: title,
        content: content,
        icon: createVNode(ExclamationCircleOutlined),
        okText: okText,
        cancelText: "Cancelar",
        onOk() {
          fnOk();
        },
        onCancel() {
          fnCancel();
        },
      });
    },
    // submit de formulario
    handleSubmit() {
      // const self = this;
      // if (this.isChangeAction && this.settings.child) {
      //   this.$confirm({
      //     title: `¿Estás seguro de cambiar la acción del mensaje`,
      //     content: "Recuerda que al cambiar la acción, los mensajes siguientes serán eliminados.",
      //     icon: createVNode(ExclamationCircleOutlined),
      //     okText: "Guardar",
      //     cancelText: "Cancelar",
      //     centered: true,
      //     onOk() {
      //       self.updateNode();
      //     },
      //   });
      //   return;
      // }

      this.updateNode();
    },
    updateNode() {
      if (this.pickerVars && typeof this.pickerVars.validateParams === 'function') {
        const isValid = this.pickerVars.validateParams();
        if (!isValid) return;
      }

      console.log('update node');
      this.form
        .validateFields()
        .then(async (values) => {
          this.loading = true;
          // para media
          if (this.mediaData) values = { ...values, ...this.mediaData };
          values.wait_answer = this.waitAnswer;
          values.contains_variable = this.formState.contains_variable || false;
          if (hasSimpleOptionConfig.includes(this.type)) {
            await this.handleUpdateOption(values);
          } else if (hasChildNodes(this.type)) {
            await this.handleUpdateOptions(values);
          } else {
            await this.handleUpdateNodeMessage(values, this.connectorSelected?.data_value);
          }

          if (this.pickerVars) this.pickerVars.saveVarInNode();
          this.$emit("update");
          this.loading = false;
          this.setIsSavedItem(true);
          this.isChangeAction = false;
        })
        .catch((err) => {
          console.error({ err });
        });
    },
    onInput() {
      // this.formState.api_input_variable = null;
      this.setIsSavedItem(false);
    },
    changePickerVars() {
      this.formState.api_input_variable = null;
    },
    splitByGrapheme(text) {
      const segmenter = new Intl.Segmenter(undefined, { granularity: 'grapheme' });
      return [...segmenter.segment(text)].map(seg => seg.segment);
    },
    removeComposedEmojis(text) {
      console.log("texto: ", text)
      const graphemes = this.splitByGrapheme(text);
      console.log("Graphemes detectados:", graphemes);
      return this.splitByGrapheme(text)
        .filter(grapheme => !this.isEmojiComposed(grapheme))
        .join('');
    },
    onInputRemoveComposed(event, fieldName) {
      console.log("event: ", event);
      if (this.formState[fieldName] !== undefined && typeof this.formState[fieldName] === 'string') {
        this.formState[fieldName] = this.removeComposedEmojis(this.formState[fieldName])
      }
      
      this.setIsSavedItem(false);
    },
    onInputRemoveEmojis(event, fieldName) {
      // Ant Design Vue puede emitir el valor o el evento completo
      const raw = typeof event === 'string'
        ? event
        : (event?.target?.value ?? '');

      // Quita emojis y caracteres pictográficos
      const cleaned = raw.replace(/\p{Extended_Pictographic}/gu, '');

      this.formState[fieldName] = cleaned;

      // Si el input viene de un evento, actualiza visualmente también
      if (event?.target) {
        event.target.value = cleaned;
      }
      this.onInputRemoveComposed(event, 'title');
    },
    disabledEmojiPicker(fieldName, type) {
      return this.formState[fieldName]?.length >= this.maxLength(fieldName, type) - 1;
    },
    maxLength(fieldName, type) {
      const maxLengthV = NodeTypeTextSize?.[type]?.[fieldName] || 0;
      
      if ([NodeType.Options, NodeType.Buttons, NodeType.List].includes(type) && fieldName == 'message') {
        return type === NodeType.Options
                        ? maxLengthV
                        : this.currentChannel?.id === ChannelType.Whatsapp
                        ? 1024
                        : maxLengthV;
      }
      return maxLengthV;
    },
    formatterCount(fieldName, type) {
      return  `${this.formState[fieldName]?.length || 0}/${this.maxLength(fieldName, type)}`
    },
    onInputButtonSanitize(event, fieldName) {
      // Soporta: letras, números, marcas combinantes (acentos), espacios,
      // punto, coma, ¿?, ¡!, guion -, emojis (Extended_Pictographic),
      // ZWJ (u200D) y variation selector (uFE0F).
      const allowedRegex =
        /[^\-\p{L}\p{N}\p{M}\p{Extended_Pictographic}\s.,!?¡¿\u200D\uFE0F]/gu;

      const raw =
        typeof event === 'string'
          ? event
          : (event?.target?.value ?? '');

      const cleaned = raw.replace(allowedRegex, '');

      // Actualiza el modelo (v-model) siempre desde el valor calculado
      this.formState[fieldName] = cleaned;

      if (event?.target) {
        event.target.value = cleaned;
      }

      this.onInputRemoveComposed(event, fieldName);

    },
    handleChangeLines() {
      this.setIsSavedItem(false);
    },
    handleQueueChange() {
      if (this.formState.queue) this.setIsSavedItem(false);
      else this.setIsSavedItem(true);
    },
    handleCancel() {
      if (this.menuOptions) this.menuOptions.resetChanges();
      if (this.menuOptionsWrong) this.menuOptionsWrong.resetChanges();
      this.mediaData = null;
      if (this.formState.type) this.formState.type = null;

      this.setValues();
      this.setIsSavedItem(true);
      this.isChangeAction = false;
      if (this.pickerVars) this.pickerVars.resetChanges();
    },
    setIsSavedItem(isSavedNode) {
      this.isSavedNode = isSavedNode;
      this.SET_IS_SAVED_NODE(this.isSavedNode);
    },
    handleClose() {
      this.handleCancel();
      this.$emit("handleCloseDrawer");
      this.SET_SELECTED_NODE_ID(null);
    },
    /**
     * Subir cambios a una opcion
     * @param {Object} values - valores del formulario
     * @param {String} values.nameOption - nombre de la opcion
     * @param {String} values.action - transfer, continue_message, continue_options, none
     * @param {String} values.queue - la cola de transferencia
     * @param {String} values.message - el mensaje de la accion
     */
    async handleUpdateOption(values) {
      let newNodeType = values.type;
      // const { transfer_queue_message } = values;
      const nodeId = this.settings.id;
      const newOption = { ...values };

      if (!this.activeMessageTransfer) newOption.transfer_queue_message = "";
      if (values.description) newOption.description = values.description;
      if (values.action) newOption.action = values.action;

      if (values.action === BotActions.AddMessage) newNodeType = NodeType.Message;
      if (values.action === BotActions.ContinueToOptions) newNodeType = NodeType.Options;
      if (values.action === BotActions.ContinueToButtons) newNodeType = NodeType.Buttons;
      if (values.action === BotActions.ContinueToCard) newNodeType = NodeType.Card;
      if (values.action === BotActions.ContinueToList) newNodeType = NodeType.List;
      if (values.action === BotActions.TransferToQueue) {
        newOption.queue = values.queue;
        newOption.portfolio = values.portfolio;
      }

      try {
        console.log('update node: ', values);
        await updateNode(nodeId, newOption);

        if (values.action !== this.settings.action) {
          const hasChild = this.settings.child.length !== 0;
          if (hasChild) await deleteNode(this.settings.child[0].id);

          if (
            [
              BotActions.AddMessage,
              BotActions.ContinueToOptions,
              BotActions.ContinueToButtons,
              BotActions.ContinueToList,
              BotActions.ContinueToCard,
            ].includes(values.action)
          ) {
            this.createNode({ nodeType: newNodeType, parentNodeId: nodeId });
          }
        }

        this.updateNodesList();

        this.$message.success("Se actualizó correctamente");
      } catch (error) {
        const errorMessage = error?.getErrorMessage?.() || "No se pudo ejecutar la acción";
        this.$message.error(errorMessage);
        console.error(error);
      }
    },
    /**
     * Subir cambios al nodo de opciones
     * @param {Object} values - valores del formulario
     * @param {String} values.message - el texto de opciones
     * @param {String} values.message_wrong_answer - el mensaje de error
     */
    async handleUpdateOptions(values) {
      const newOptionsBody = { ...values };

      try {
        console.log('update node: ', values);
        await updateNode(this.settings.id, newOptionsBody);

        this.$message.success("Se actualizó correctamente");
      } catch (error) {
        const errorMessage = error?.getErrorMessage() || "No se pudo ejecutar la acción";
        this.$message.error(errorMessage);
        console.error(error);
      }

      this.updateNodesList();
    },
    bodyOptionConnectorApi(order, option_value) {
      let node = { description: 'El valor a devolver de la API' };
      console.log('bodyOptionConnectorApi:', order, option_value)
      if (order) {
        if (option_value == 'false')
          node.option = '0';  
        else if (option_value == 'true')
          node.option = '1';
        else
          node.option = String(option_value);

        node.order = order;
        let referencial_name = ''
        if (option_value == 'true') referencial_name = 'Verdadero'
        else if (option_value == 'false') referencial_name = 'False'
        else referencial_name = 'Opción ' + option_value

        console.log(referencial_name, 'referencial_name');
        node.referencial_name = referencial_name;
      }
      console.log('node:', node)
      return node;
    },
    async createOptionsConnectorApi(dataValue, newNodeType, nodeId) {
      if (this.connectorSelected?.data_type == ResponseType.String) {
        console.log('this.formState?.action_next: ', this.formState?.action_next);
        let newNodeType = this.getNewNodeType(this.formState?.action_next);
        console.log('newNodeType: ', newNodeType);
        await this.createNode({
          nodeType: newNodeType,
          parentNodeId: nodeId,
          previousAction: BotActions.ContinueConnectorApi,
        });
      } else {
        for (const [pos, el] of dataValue.entries()) {
          const bodyOption = this.bodyOptionConnectorApi(pos + 1, el);

          await this.createNode({
            nodeType: newNodeType,
            parentNodeId: nodeId,
            previousAction: BotActions.ContinueConnectorApi,
            newBody: {
              action: BotActions.FinishChatbot,
              type: newNodeType,
              node: nodeId,
              ...bodyOption,
            }
          });
        }
      }

      await this.createNode({
        nodeType: NodeType.WrongOptionAPI,
        parentNodeId: nodeId,
        previousAction: BotActions.ContinueConnectorApi,
      });
    },
    getNewNodeType(action, typeDefault = null) {
      let newNodeType = typeDefault;

      if (action === BotActions.AddMessage) newNodeType = NodeType.Message;
      if (action === BotActions.ContinueToOptions) newNodeType = NodeType.Options;
      if (action === BotActions.ContinueToButtons) newNodeType = NodeType.Buttons;
      if (action === BotActions.ContinueToCard) newNodeType = NodeType.Card;
      if (action === BotActions.ContinueToList) newNodeType = NodeType.List;
      if (action === BotActions.ContinueConnectorApi) newNodeType = NodeType.OptionConsultApi;

      console.log('action - newNodeType: ', action, newNodeType)
      return newNodeType;
    },
    /**
     * Subir cambios al nodo de mensaje
     * @param {Object} values - valores del formulario
     * @param {String} values.message - texto del mensaje
     * @param {Boolean} values.wait_answer - si el mensaje esperará una respuesta o no
     * @param {String} values.queue - la cola de transferencia
     * @param {String} values.action - transfer, continue, none
     */    
    async handleUpdateNodeMessage(values, dataValue = []) {
      let newNodeType = values.type;
      const nodeId = this.settings.id;
      const newMessageNode = { ...values };
      if (this.formState.type) newMessageNode.type = this.formState.type;

      newNodeType = this.getNewNodeType(values.action, newNodeType);
      // if (values.action === BotActions.AddMessage) newNodeType = NodeType.Message;
      // if (values.action === BotActions.ContinueToOptions) newNodeType = NodeType.Options;
      // if (values.action === BotActions.ContinueToButtons) newNodeType = NodeType.Buttons;
      // if (values.action === BotActions.ContinueToCard) newNodeType = NodeType.Card;
      // if (values.action === BotActions.ContinueToList) newNodeType = NodeType.List;
      // if (values.action === BotActions.ContinueConnectorApi) newNodeType = NodeType.OptionConsultApi;

      if (values.action === BotActions.TransferToQueue) {
        newMessageNode.queue = values.queue;
        newMessageNode.portfolio = values.portfolio;
      }

      try {
        await updateNode(nodeId, newMessageNode);

        console.log('this.settings: ', this.settings);
        if (values.action !== this.settings.action && values.action != BotActions.ContinueConnectorApi) {
          const hasChild = this.settings.child.length !== 0;
          if (hasChild && this.settings.action != BotActions.ContinueConnectorApi) {
            await deleteNode(this.settings.child[0].id);
          } else if (hasChild && this.settings.action == BotActions.ContinueConnectorApi) {
            for (const el of this.settings.child) {
              await deleteNode(el.id);
            }
            this.settings.child = [];
          }

          if (
            [
              BotActions.AddMessage,
              BotActions.ContinueToOptions,
              BotActions.ContinueToButtons,
              BotActions.ContinueToList,
              BotActions.ContinueToCard,
            ].includes(values.action)
          ) {
            this.createNode({
              nodeType: newNodeType,
              parentNodeId: nodeId,
              previousAction: this.settings.action,
            });
          }
        }
        
        if (values.action == BotActions.ContinueConnectorApi) {
          const newNodeBody = values;

          // Si la nueva accion es diferente a la anterior se crean las opciones y una opcion incorrecta de api
          if (newNodeBody.action != this.settings.action) {
            for (const el of this.settings.child) {
              await deleteNode(el.id);
            }
            this.settings.child = [];

            if (dataValue) await this.createOptionsConnectorApi(dataValue, newNodeType, nodeId);              
          }

          // Si la nueva accion es la misma de la anterior, se verifica si cambio el connector api
          else if (
            newNodeBody.api_connector != this.settings.api_connector?.id ||
            newNodeBody.action_next != this.settings.action_next
          ) {
            // borramos las opciones anterior y creamos otras de nuevo
            for (const el of this.settings.child) {
              await deleteNode(el.id);
            }

            if (dataValue) await this.createOptionsConnectorApi(dataValue, newNodeType, nodeId);
          }
        }

        this.updateNodesList();

        this.$message.success("Se actualizó correctamente");
      } catch (error) {
        console.error(error);
        const errorMessage = error?.getErrorMessage() || "No se pudo ejecutar la acción";
        this.$message.error(errorMessage);
      }
    },
    /**
     * @param {Object} nodeOptions
     * @param {Number} nodeOptions.nodeType
     * @param {Number} nodeOptions.parentNodeId
     */
    async createNode(nodeOptions = {}) {
      this.$emit("create-node", nodeOptions);
    },
    updateNodesList() {
      this.$emit("update-nodes");
    },
    /**
     * Cambia el valor de wait
     * @param {Boolean} wait - espera respuesta o no
     */
    handleChangeWait(wait) {
      this.formState.wait_answer = wait;
      this.waitAnswer = wait;
      this.setIsSavedItem(false);
    },
    /**
     * Quita el check del 'Esperar respuesta'
     */
    uncheckWaitAnswer() {
      this.formState.wait_answer = false;
      this.waitAnswer = false;
    },
    handleChangeMedia(mediaData) {
      this.setIsSavedItem(false);
      this.formState.type = mediaData.type;
      // si existe media, agregará datos al existente
      if (this.mediaData)
        this.mediaData = {
          ...this.mediaData,
          ...mediaData.media,
        };
      else this.mediaData = mediaData.media;
    },
    /**
     * Setea un valor para contains variable
     * @param {Boolean} checked
     */
    handleSetContainsVariable(checked) {
      this.setIsSavedItem(false);
      this.formState.contains_variable = checked;
      this.formState.api_input_variable = null;
      // el valor de esperar respuesta es requerido cuando checked cambia
      this.handleChangeWait(checked);
    },
    handleSetEmoji(emoji, field) {
      this.formState[field] = this.formState[field] += emoji.i;
      this.setIsSavedItem(false);
    },
    handleOpenVars(field) {
      let text = this.formState[field];
      const textArray = text.split("");
      // si el ultimo valor no es el prefijo de variables, seteará el prefijo al texto
      if (textArray.pop() !== this.PREFIX_VARS) {
        text = text + this.PREFIX_VARS;
        this.formState[field] = text;
      }
      // Importante no borrar, porque si no, no renderizara el listado de variables
      setTimeout(() => {
        this.simulateKey(field, PREFIX_VARS);
      }, 0);
    },
    /**
     * Simula el #
     * @param {String} field - nombre del campo
     * @param {Number} keyCode The keyCode of the key to simulate
     * @param {String} type (optional) The type of event : down, up or press. The default is down
     * @param {Object} modifiers (optional) An object which contains modifiers keys { ctrlKey: true, altKey: false, ...}
     */
    simulateKey(field, keyCode, type = "up", modifiers) {
      const mentions = document.getElementById(`form_item_${field}`);
      const evtName = typeof type === "string" ? "key" + type : "keydown";
      const modifier = typeof modifiers === "object" ? modifier : {};
      const event = document.createEvent("HTMLEvents");
      event.initEvent(evtName, true, false);
      event.key = keyCode;
      for (const i in modifiers) {
        event[i] = modifiers[i];
      }
      mentions.dispatchEvent(event);
    },
  },
});
</script>

<style lang="sass" scoped>
$size_option: 28px
=flex($direction, $content, $align)
  display: flex
  flex-direction: $direction
  justify-content: $content
  align-items: $align
:deep(.ant-form-item)
  margin-bottom: 12px

.text-align-left
  text-align: left
.drawer-settings
  height: 100%
  background-color: #FFF
  border: 1px solid $gray-5
  border-radius: 8px
  position: relative

  &-header
    padding: 12px
    border-bottom: 1px solid $gray-5
    display: flex
    flex-direction: row
    justify-content: space-between
    align-items: center
    .title
      color: $gray-9
      text-align: left
      margin-bottom: 0px
      font-size: 14px

  &-body
    height: calc(100% - 44px)

    .scroll
      height: calc(100% - 60px)
      padding: 12px 16px

    .help
      color: $gray-7
      margin-top: -8px
    .switch-form
      position: absolute
      right: 12px
      transform: translateY(3px)
      z-index: 1
  &-footer
    padding: 12px
    border-top: 1px solid $gray-5
    width: 100%
    display: flex
    flex-direction: row
    justify-content: flex-end
    position: absolute
    bottom: 0
    background-color: white
.section-form-item--message
  margin-bottom: 0px
.radio-message
  display: flex
  flex-direction: row
  align-items: center
  &__icon
    font-size: 14px

.body-2
  color: $gray-7

.loading
  display: grid
  place-content: center
  height: calc(100% - 50px)

.divider
  background-color: $gray-4
  margin: 24px 0

:global(.ant-form-item-control-input-content .item-emoji)
  display: flex !important
  margin-right: 2px !important
  align-items: center
.menu
  &__option
    padding: 6px
    background-color: $gray-3
    width: $size_option
    margin-right: 4px
    border-radius: 4px
    height: $size_option
    @include flex(row, center, center)
    cursor: pointer
    color: $text-secondary

.select--info
  color: $text-tertiary
  font-size: 12px
  margin: 0

.select-empty-custom
  display: flex
  flex-direction: column
  align-items: center
  justify-content: center
  padding: 0
  margin: 0
  // padding: 16px 0
  text-align: center

  .ant-empty
    margin-top: 8px
    margin-bottom: 8px

  &__text
    font-size: 14px
    color: #8c8c8c
    // line-height: 20px
</style>
